import React, { useMemo } from 'react';
import { ConfigProvider, theme as antdTheme, Button, Tag, Typography } from 'antd';
import { buildAntdTheme } from '../utils/theme';
import type { ThemeConfig } from '../../types';

const { Text } = Typography;

interface ThemeLivePreviewProps {
    theme: ThemeConfig;
    name?: string;
    compact?: boolean;
}

const PreviewBody: React.FC<{ theme: ThemeConfig; name?: string }> = ({ theme, name }) => {
    const { token } = antdTheme.useToken();

    const cardBg = theme.cardTransparency
        ? (theme.isDark ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.55)')
        : token.colorBgContainer;

    const colWidth = Math.min(140, Math.max(88, Math.round((theme.cardWidth || 300) * 0.32)));

    const shellStyle: React.CSSProperties = {
        fontFamily: token.fontFamily,
        fontSize: Math.max(11, Math.min(14, token.fontSize - 2)),
        color: token.colorText,
        backgroundColor: token.colorBgLayout,
        backgroundImage: theme.backgroundImage ? `url(${theme.backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        // Preview chrome radius stays fixed; theme.borderRadius only affects inner UI
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${token.colorBorderSecondary}`,
        position: 'relative',
    };

    return (
        <div className="theme-live-preview" style={shellStyle}>
            {theme.backgroundImage ? (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: theme.isDark ? 'rgba(0,0,0,0.38)' : 'rgba(255,255,255,0.42)',
                        pointerEvents: 'none',
                    }}
                />
            ) : null}

            <div className="theme-live-preview__inner">
                <div
                    className="theme-live-preview__topbar"
                    style={{
                        background: theme.isDark ? 'rgba(20,20,20,0.78)' : 'rgba(255,255,255,0.82)',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        backdropFilter: 'blur(8px)',
                    }}
                >
                    <Text style={{ color: token.colorTextSecondary, fontSize: 'inherit' }}>
                        {name?.trim() || 'Проекты'} / эпик
                    </Text>
                    <Button type="primary" size="small">
                        Задача
                    </Button>
                </div>

                <div className="theme-live-preview__board">
                    {['Открыт', 'В работе'].map((title, colIdx) => (
                        <div
                            key={title}
                            className="theme-live-preview__col"
                            style={{
                                width: colWidth,
                                borderRadius: token.borderRadius,
                                background: token.colorFillQuaternary,
                                border: `1px solid ${token.colorBorderSecondary}`,
                            }}
                        >
                            <div className="theme-live-preview__col-head">
                                <span style={{ fontWeight: 600, color: token.colorText }}>{title}</span>
                                <span
                                    style={{
                                        background: token.colorFillSecondary,
                                        borderRadius: 999,
                                        padding: '0 6px',
                                        fontSize: 10,
                                        color: token.colorTextSecondary,
                                    }}
                                >
                                    {colIdx === 0 ? 2 : 1}
                                </span>
                            </div>

                            {[0, 1].slice(0, colIdx === 0 ? 2 : 1).map((cardIdx) => (
                                <div
                                    key={cardIdx}
                                    className="theme-live-preview__card"
                                    style={{
                                        background: cardBg,
                                        color: token.colorText,
                                        borderRadius: Math.max(2, token.borderRadius - 2),
                                        border: `1px solid ${token.colorPrimary}33`,
                                        backdropFilter: theme.cardTransparency ? 'blur(8px)' : undefined,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                    }}
                                >
                                    <div className="theme-live-preview__card-meta">
                                        <span
                                            style={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: '50%',
                                                background: token.colorPrimary,
                                                display: 'inline-block',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Tag
                                            color={cardIdx === 0 ? token.colorError : token.colorWarning}
                                            style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                                        >
                                            {cardIdx === 0 ? 'Баг' : 'UI'}
                                        </Tag>
                                    </div>
                                    <div style={{
                                        lineHeight: 1.35,
                                        fontWeight: theme.regularCardTitles ? 400 : 600,
                                    }}>
                                        {cardIdx === 0 ? 'Пример задачи с тегом' : 'Ещё одна карточка'}
                                    </div>
                                    {cardIdx === 0 && (
                                        <Text style={{ color: token.colorLink, fontSize: 10 }}>
                                            ссылка
                                        </Text>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className="theme-live-preview__swatches">
                    {[
                        token.colorPrimary,
                        token.colorSuccess,
                        token.colorWarning,
                        token.colorError,
                        token.colorInfo,
                    ].map((c) => (
                        <span
                            key={c}
                            title={c}
                            style={{
                                width: 14,
                                height: 14,
                                borderRadius: Math.min(token.borderRadius, 6),
                                background: c,
                                border: `1px solid ${token.colorBorderSecondary}`,
                            }}
                        />
                    ))}
                    <Text type="secondary" style={{ fontSize: 10, marginLeft: 4 }}>
                        {theme.isDark ? 'Тёмная' : 'Светлая'}
                        {theme.isCompact ? ' · компакт' : ''}
                        {theme.cardTransparency ? ' · стекло' : ''}
                    </Text>
                </div>
            </div>
        </div>
    );
};

/** Live mini-shell using the same Ant token pipeline as the real app */
const ThemeLivePreview: React.FC<ThemeLivePreviewProps> = ({ theme, name }) => {
    const antdThemeConfig = useMemo(() => {
        const config = buildAntdTheme(theme);
        // Unique cssVar key — same key as the app (`vdelo`) would overwrite global tokens
        return {
            ...config,
            cssVar: { key: 'vdelo-preview' as const },
        };
    }, [theme]);

    return (
        <ConfigProvider theme={antdThemeConfig}>
            <PreviewBody theme={theme} name={name} />
        </ConfigProvider>
    );
};

export default ThemeLivePreview;
